import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

/**
 * Dumb, presentational renderer for the classroom pause / "no slide selected"
 * fallback image — a static, non-interactive `<img>`. It is NOT a fabric slide:
 * the pause image is chrome (a break/idle placeholder), has no drawing surface,
 * no hotspots, and no synced state.
 *
 * Data in / event out: the parent passes a final display URL (it already applied
 * this client's quality variant) and receives only a load-`error`. Keeping the
 * quality/selection logic in the parent lets this component stay free of any
 * `@pcp/*` data dependency, which a `type:ui` lib requires.
 *
 * Lives in `slide-rendering` — the one lib for all slide-type
 * UI; the fabric renderer is its sibling here.
 */
@Component({
    selector: 'pcp-pause-image',
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `<img [src]="imageUrl()" [alt]="alt()" (error)="loadError.emit($event)" />`,
    styles: [
        `
            :host {
                display: block;
                height: 100%;
                max-width: 100%;
            }
            img {
                display: block;
                height: 100%;
                max-width: 100%;
                width: auto;
                object-fit: contain;
            }
        `,
    ],
})
export class PauseImageComponent {
    /** Final display URL (the parent already applied the per-client quality variant). */
    readonly imageUrl = input.required<string>();
    /** Decorative by default — the pause screen conveys no unique information to assistive tech. */
    readonly alt = input('');
    /** Emitted if the image fails to load (the parent may log it). Named `loadError`, not `error`,
     *  to avoid shadowing the native DOM `error` event (`@angular-eslint/no-output-native`). */
    readonly loadError = output<Event>();
}
